// import react packages;
import React, { useContext, useState } from "react";
import { useMutation } from "@apollo/client";
import PropTypes from "prop-types";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

// Import Polaris Components
import { PageActions, InlineStack, TextField, Button } from "@shopify/polaris";

// import helper
import { OnboardingContext, PrivateContext } from "lib/context";
import { baseHelper, errorHelper } from "lib/helpers";
import constant from "lib/constant/constant";

import { CONNECT_PRESTASHOP } from "app/product/apollo/mutations";

import SyncPrestaShopProduct from "app/product/modules/provider/features/add/subFeatures/syncPrestaShopProduct";

// import components
import TermModal from "app/product/modules/provider/features/genric/term/term";
// import newCMSData from "app/v1/products/modules/provider/features/add/subFeatures/wixConnect/cms/cms.json";
import { useNavigate } from "react-router-dom";
import ConnectedShop from "./subFeatureItems/connected";
import DiscoveryTermModal from "../term/discoveryTerm";
import HelpModal from "./subFeatureItems/help";

const VendorConnectPrestaShop = (props) => {
  const { currentUser: currentUserData, cms } = useContext(PrivateContext);
  const navigate = useNavigate();

  const { prestaShop: prestaConfig = {} } = (currentUserData && currentUserData.storeConfig) || {};
  const { shop = "", accessKey = "" } = prestaConfig || {};
  const { isOnboarding, onPrevious } = useContext(OnboardingContext);
  const { CRITICAL, SUCCESS } = constant;
  const { setBanner } = props;
  const [value, setValue] = useState({
    accessKey: accessKey || "",
    shopDomain: shop || "",
  });
  const [isConnected, setIsConnected] = useState(!!shop);
  const [isOpen, setIsOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    shopDomain: "",
    accessKey: "",
  });

  const isProductDiscovery = !!currentUserData?.isProductDiscovery;

  const [connectPrestaShop, { loading }] = useMutation(CONNECT_PRESTASHOP);
  const { CONNECT_PRESTASHOP: STORE_CONNECT } = constant.gql;

  const onSubmit = async () => {
    const { accessKey = "", shopDomain = "" } = value;
    // const isValidDomainPath = baseHelper.isValidDomainPath(shopDomain.trim());
    // if (!isValidDomainPath) {
    //   setBanner({ isOpen: true, status: CRITICAL, title: cms("section.connect.message.error.invalidShop") });
    //   setIsOpen(false);
    //   return;
    // }
    try {
      const response = await connectPrestaShop({
        variables: {
          input: {
            accessKey: (accessKey || "").trim(),
            shopDomain: (shopDomain || "").trim(),
          },
        },
      });
      const responseError = baseHelper.getResponseError(response.data, STORE_CONNECT);
      if (responseError) {
        setBanner({ isOpen: true, status: CRITICAL, title: responseError });
        setIsOpen(false);
        return;
      }
      const responseData = baseHelper.getResponseData(response.data, STORE_CONNECT);
      if (responseData) {
        setIsConnected(true);
        setBanner({ isOpen: true, status: SUCCESS, title: `${cms("section.connect.message.success.connected")}` });
        setIsOpen(false);
        return;
      }
    } catch (exception) {
      setBanner({ isOpen: true, status: CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const handleChange = (fieldName, fieldValue) => {
    setValue({
      ...value,
      [fieldName]: (fieldValue || "").trim(),
    });
  };

  const handleTermModal = (fieldName = "check", modalValue = "") => {
    if (!value.shopDomain || !value.accessKey) {
      setIsOpen(false);
      const errorMessages = {
        accessKey: "Access Key is Required",
        shopDomain: cms("section.connect.message.error.domain"),
      };
      setErrorMessage({
        ...errorMessage,
        [fieldName]: !value[fieldName] ? errorMessages[fieldName] : "",
      });

      return;
    }
    if (modalValue === constant.CONNECT) {
      setIsOpen(true);
    }
    setErrorMessage("");
  };

  return (
    <>
      <HelpModal
        cms={cms}
        isOpen={isHelpOpen}
        setIsOpen={setIsHelpOpen}
        shopDomain={value.shopDomain}
        setBanner={setBanner}
        loading={loading}
        onSubmit={() => setIsHelpOpen(false)}
        item="Presta Shop"
      />
      {isProductDiscovery ? (
        <DiscoveryTermModal
          cms={cms}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          shopDomain={value.shopDomain}
          setBanner={setBanner}
          loading={loading}
          onSubmit={onSubmit}
          item="Presta Shop"
        />
      ) : (
        <TermModal
          cms={cms}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          shopDomain={value.shopDomain}
          setBanner={setBanner}
          loading={loading}
          onSubmit={onSubmit}
          item="Presta Shop"
        />
      )}
      <TextField
        label={`${cms("section.connect.label.shopDomain")}*`}
        error={errorMessage.shopDomain}
        id="shopInput"
        prefix="https://"
        placeholder={cms("section.connect.placeholder.demo")}
        type={constant.TEXT}
        value={value.shopDomain}
        onChange={(inputValue) => handleChange(constant.SHOP_DOMAIN, inputValue)}
        onBlur={() => handleTermModal(constant.SHOP_DOMAIN)}
        disabled={isConnected}
      />
      {!isConnected ? (
        <>
          <br />
          <TextField
            label="Access Key"
            error={errorMessage.accessKey}
            id="accessKey"
            placeholder="Access Key"
            type={constant.TEXT}
            value={value.accessKey}
            onChange={(inputValue) => handleChange(constant.ACCESS_KEY, inputValue)}
            onBlur={() => handleTermModal(constant.ACCESS_KEY)}
            disabled={isConnected}
            helpText={(
              <Button plain onClick={() => setIsHelpOpen(true)}>
                Generate your Presta Shop access token
              </Button>
            )}
          />
        </>
      ) : null}
      {isConnected && (
        <>
          <br />
          <InlineStack>
            <ConnectedShop
              cms={cms}
              currentUserData={currentUserData}
              setBanner={setBanner}
              setIsConnected={setIsConnected}
              setValue={setValue}
              value={value}
            />
            <SyncPrestaShopProduct navigate={navigate} setBanner={setBanner} cms={cms} />
          </InlineStack>
        </>
      )}
      <br />
      {!isConnected && (
        <PageActions
          primaryAction={{
            id: "connectShop",
            content: `${cms("section.connect.button.connect")}`,
            onAction: () => handleTermModal("check ", constant.CONNECT),
            disabled: isConnected,
          }}
          secondaryActions={[
            {
              id: "cancelAction",
              content: `${cms("section.connect.button.cancel")}`,
              onAction: () => (isOnboarding && onPrevious()) || navigate("/products"),
            },
          ]}
        />
      )}
    </>
  );
};

VendorConnectPrestaShop.propTypes = {
  setBanner: PropTypes.func,
};

VendorConnectPrestaShop.defaultProps = {
  setBanner: () => {},
};

export default withFeature(withErrorBoundary(VendorConnectPrestaShop), {
  feature: constant.PRODUCT_IMPORT_WOOCOMMERCE,
  // subFeature: constant.PRODUCT_IMPORT_WOOCOMMERCE,
});
